import React from 'react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isToday, 
  isSameDay,
  addMonths,
  subMonths,
  startOfDay,
  addWeeks,
  subWeeks,
  startOfWeek as startOfWeekFn,
  endOfWeek as endOfWeekFn
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Clock, User, Calendar } from 'lucide-react';
import { RendezVous } from '../../types';
import StatusChangeButton from './StatusChangeButton';

interface CalendarViewProps {
  rendezVous: RendezVous[];
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onRendezVousClick: (rdv: RendezVous) => void;
  onStatusChange: (id: number, status: string) => void;
  view: 'month' | 'week' | 'day';
  onViewChange: (view: 'month' | 'week' | 'day') => void;
}

export default function CalendarView({
  rendezVous,
  selectedDate,
  onDateSelect,
  onRendezVousClick,
  onStatusChange,
  view,
  onViewChange
}: CalendarViewProps) {
  /* La période affichée est pilotée par `selectedDate`, la date que le parent
     utilise aussi pour calculer la plage envoyée à l'API. Une copie locale
     désynchronisait les deux : le calendrier montrait un mois (ou un jour) que
     le parent n'avait pas chargé, d'où une grille vide après navigation. */
  const currentDate = selectedDate;

  // Navigation — remonte au parent pour que les données suivent l'affichage
  const handlePrevious = () => {
    if (view === 'month') {
      onDateSelect(subMonths(currentDate, 1));
    } else if (view === 'week') {
      onDateSelect(subWeeks(currentDate, 1));
    } else {
      onDateSelect(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000));
    }
  };

  const handleNext = () => {
    if (view === 'month') {
      onDateSelect(addMonths(currentDate, 1));
    } else if (view === 'week') {
      onDateSelect(addWeeks(currentDate, 1));
    } else {
      onDateSelect(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000));
    }
  };

  const handleToday = () => {
    onDateSelect(new Date());
  };

  // Obtenir les rendez-vous pour une date donnée
  const getRendezVousForDate = (date: Date) => {
    return rendezVous.filter(rdv => 
      isSameDay(new Date(rdv.date_heure), date)
    );
  };

  // Obtenir le titre de la période
  const getPeriodTitle = () => {
    if (view === 'month') {
      return format(currentDate, 'MMMM yyyy', { locale: fr });
    } else if (view === 'week') {
      const start = startOfWeekFn(currentDate, { weekStartsOn: 1 });
      const end = endOfWeekFn(currentDate, { weekStartsOn: 1 });
      return `${format(start, 'd MMM', { locale: fr })} - ${format(end, 'd MMM yyyy', { locale: fr })}`;
    } else {
      return format(currentDate, 'EEEE d MMMM yyyy', { locale: fr });
    }
  };

  // Obtenir la couleur selon le statut
  const getStatusColor = (status: string) => {
    const colors = {
      'attente': 'bg-blue-100 border-blue-300 text-blue-700',
      'confirme': 'bg-green-100 border-green-300 text-green-700',
      'realise': 'bg-gray-100 border-gray-300 text-gray-700',
      'annule': 'bg-red-100 border-red-300 text-red-700',
      'non_honore': 'bg-orange-100 border-orange-300 text-orange-700'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 border-gray-300 text-gray-700';
  };

  // Obtenir le label du statut
  const getStatusLabel = (status: string) => {
    const labels = {
      'attente': 'En attente',
      'confirme': 'Confirmé',
      'realise': 'Réalisé',
      'annule': 'Annulé',
      'non_honore': 'Non honoré'
    };
    return labels[status as keyof typeof labels] || status;
  };

  // Vue mensuelle
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    return (
      <div className="bg-white rounded-lg shadow">
        {/* En-têtes des jours */}
        <div className="grid grid-cols-7 gap-px border-b">
          {weekDays.map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-700 bg-gray-50">
              {day}
            </div>
          ))}
        </div>

        {/* Grille du calendrier */}
        <div className="grid grid-cols-7 gap-px">
          {days.map(day => {
            const dayRdv = getRendezVousForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isSelected = isSameDay(day, selectedDate);
            const isDayToday = isToday(day);

            return (
              <div
                key={day.toString()}
                className={`min-h-32 p-2 cursor-pointer border-r border-b hover:bg-gray-50 ${
                  !isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white'
                } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => onDateSelect(day)}
              >
                <div className={`text-sm font-medium mb-1 ${
                  isDayToday ? 'bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center' : ''
                }`}>
                  {format(day, 'd')}
                </div>
                
                <div className="space-y-1">
                  {dayRdv.slice(0, 3).map(rdv => (
                    <div
                      key={rdv.id}
                      className={`text-xs p-1 rounded border ${getStatusColor(rdv.status)} cursor-pointer hover:opacity-80`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRendezVousClick(rdv);
                      }}
                    >
                      <div className="font-medium truncate">
                        {format(new Date(rdv.date_heure), 'HH:mm')} - {rdv.patient_details?.nom}
                      </div>
                    </div>
                  ))}
                  {dayRdv.length > 3 && (
                    <div className="text-xs text-gray-500 font-medium">
                      +{dayRdv.length - 3} autres
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Vue hebdomadaire
  const renderWeekView = () => {
    const weekStart = startOfWeekFn(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeekFn(currentDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
    
    const hours = Array.from({ length: 12 }, (_, i) => i + 8); // 8h à 19h

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* En-têtes des jours */}
        <div className="grid grid-cols-8 gap-px border-b">
          <div className="p-3 text-sm font-medium text-gray-700 bg-gray-50"></div>
          {days.map(day => (
            <div key={day.toString()} className="p-3 text-center bg-gray-50">
              <div className="text-sm font-medium text-gray-700">
                {format(day, 'EEE', { locale: fr })}
              </div>
              <div className={`text-lg font-bold ${
                isToday(day) ? 'bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto' : 'text-gray-900'
              }`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>

        {/* Grille horaire */}
        <div className="grid grid-cols-8 gap-px">
          {hours.map(hour => (
            <React.Fragment key={hour}>
              <div className="p-2 text-xs text-gray-500 bg-gray-50 border-r">
                {hour}:00
              </div>
              {days.map(day => {
                const dayRdv = getRendezVousForDate(day).filter(rdv => {
                  const rdvHour = new Date(rdv.date_heure).getHours();
                  return rdvHour === hour;
                });

                return (
                  <div key={`${day}-${hour}`} className="min-h-16 p-1 border-r border-b hover:bg-gray-50">
                    {dayRdv.map(rdv => (
                      <div
                        key={rdv.id}
                        className={`text-xs p-1 rounded mb-1 border ${getStatusColor(rdv.status)} cursor-pointer hover:opacity-80`}
                        onClick={() => onRendezVousClick(rdv)}
                      >
                        <div className="font-medium truncate">
                          {rdv.patient_details?.nom}
                        </div>
                        <div className="text-gray-600 truncate">
                          {rdv.motif}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Vue journalière
  const renderDayView = () => {
    const dayRdv = getRendezVousForDate(currentDate).sort(
      (a, b) => new Date(a.date_heure).getTime() - new Date(b.date_heure).getTime()
    );

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {format(currentDate, 'EEEE d MMMM yyyy', { locale: fr })}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {dayRdv.length} rendez-vous programmé{dayRdv.length > 1 ? 's' : ''}
          </p>
        </div>

        <div className="divide-y divide-gray-200">
          {dayRdv.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Aucun rendez-vous programmé pour cette journée</p>
            </div>
          ) : (
            dayRdv.map(rdv => (
              <div
                key={rdv.id}
                className="p-6 hover:bg-gray-50 cursor-pointer"
                onClick={() => onRendezVousClick(rdv)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-1" />
                        {format(new Date(rdv.date_heure), 'HH:mm')}
                      </div>
                      <StatusChangeButton
                        currentStatus={rdv.status}
                        onStatusChange={(newStatus) => onStatusChange(rdv.id, newStatus)}
                      />
                    </div>
                    
                    <div className="flex items-center space-x-4 mb-2">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {rdv.patient_details?.nom} {rdv.patient_details?.prenoms}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {rdv.doctor_details?.user_details ? (
                          `Dr. ${rdv.doctor_details.user_details.first_name} ${rdv.doctor_details.user_details.last_name}`
                        ) : 'Médecin non assigné'}
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <strong>Motif:</strong> {rdv.motif}
                    </div>
                    
                    {rdv.notes && (
                      <div className="mt-1 text-sm text-gray-500">
                        <strong>Notes:</strong> {rdv.notes}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header avec navigation et vues */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <button
            onClick={handlePrevious}
            className="p-2 rounded-md border text-gray-500 border-gray-300 hover:bg-gray-50"
            title="Mois/semaine/jour précédent"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          
          <h2 className="text-xl font-semibold text-gray-900 min-w-64 text-center">
            {getPeriodTitle()}
          </h2>
          
          <button
            onClick={handleNext}
            className="p-2 rounded-md border text-gray-500 border-gray-300 hover:bg-gray-50"
            title="Mois/semaine/jour suivant"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          
          <button
            onClick={handleToday}
            className="px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md"
          >
            Aujourd'hui
          </button>
        </div>

        {/* Sélecteur de vues */}
        <div className="flex rounded-md border border-gray-300 overflow-hidden">
          {[
            { key: 'month', label: 'Mois' },
            { key: 'week', label: 'Semaine' },
            { key: 'day', label: 'Jour' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => onViewChange(key as 'month' | 'week' | 'day')}
              className={`px-4 py-2 text-sm font-medium ${
                view === key
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu du calendrier */}
      <div>
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'day' && renderDayView()}
      </div>
    </div>
  );
}